import { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Stack,
  Snackbar,
  Alert,
  Link
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckIcon from "@mui/icons-material/Check";

const MONO_STACK = '"JetBrains Mono", "SF Mono", Menlo, Monaco, Consolas, monospace';
const DOCS_URL = "https://datastar.software/docs/ci-cd/datastar-tools";

export default function LicenseKeyCard({ licenseKey, licenseId }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [snackbar, setSnackbar] = useState("");

  if (!licenseKey) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(licenseKey);
      setCopied(true);
      setSnackbar("Licence key copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setSnackbar("Couldn't copy — your browser blocked clipboard access");
    }
  };

  const handleDownload = () => {
    const blob = new Blob([licenseKey], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = licenseId ? `datastar-${licenseId}.key` : "datastar.key";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloaded(true);
    setSnackbar("Licence key downloaded");
    setTimeout(() => setDownloaded(false), 2000);
  };

  return (
    <>
      <Card>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                DataStar.Tools licence key
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                Pass this to{" "}
                <Box component="code" sx={{ fontFamily: MONO_STACK, fontSize: "0.85em", px: 0.5 }}>
                  DataStar.Tools
                </Box>{" "}
                via the{" "}
                <Box component="code" sx={{ fontFamily: MONO_STACK, fontSize: "0.85em", px: 0.5 }}>
                  --license-key
                </Box>{" "}
                (or{" "}
                <Box component="code" sx={{ fontFamily: MONO_STACK, fontSize: "0.85em", px: 0.5 }}>
                  -lk
                </Box>
                ) flag to activate CLI deployments. Store it in your CI runner's secrets — don't commit it. See the{" "}
                <Link href={DOCS_URL} target="_blank" rel="noopener">DataStar.Tools docs</Link> for usage.
              </Typography>
            </Box>

            {revealed ? (
              <>
                <Box
                  sx={{
                    fontFamily: MONO_STACK,
                    fontSize: "0.85rem",
                    lineHeight: 1.6,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: (t) => (t.palette.mode === "dark" ? "#0f0f0f" : "#f5f5f5"),
                    border: 1,
                    borderColor: "divider",
                    wordBreak: "break-all",
                    whiteSpace: "pre-wrap",
                    userSelect: "all"
                  }}
                >
                  {licenseKey}
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                    variant="contained"
                    color={copied ? "success" : "primary"}
                    onClick={handleCopy}
                    sx={{ minWidth: 160 }}
                  >
                    {copied ? "Copied" : "Copy key"}
                  </Button>
                  <Button
                    startIcon={downloaded ? <CheckIcon /> : <DownloadIcon />}
                    variant="outlined"
                    onClick={handleDownload}
                    sx={{ minWidth: 160 }}
                  >
                    {downloaded ? "Downloaded" : "Download"}
                  </Button>
                  <Button
                    startIcon={<VisibilityOffIcon />}
                    variant="text"
                    color="inherit"
                    onClick={() => setRevealed(false)}
                    sx={{ ml: { sm: "auto !important" } }}
                  >
                    Hide
                  </Button>
                </Stack>
              </>
            ) : (
              <Button
                startIcon={<VisibilityIcon />}
                variant="contained"
                onClick={() => setRevealed(true)}
                sx={{ alignSelf: "flex-start", minWidth: 160 }}
              >
                Show key
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={2500}
        onClose={() => setSnackbar("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSnackbar("")}>
          {snackbar}
        </Alert>
      </Snackbar>
    </>
  );
}
